import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import { useCallback, useState } from 'react';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Button,
  Divider,
  Link,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';

import { RouterLink } from 'src/routes/components';

import { fDateTime } from 'src/utils/format-time';
import { findProducts } from 'src/utils/products';
import { hasPermission } from 'src/utils/has-permissions';
import { TABLE_HEAD_CELL_TYPE, TRANSACTION_STATUS } from 'src/utils/common-types';

import { useAuthContext } from 'src/auth/hooks';
import { useAppDispatcher } from 'src/redux/store';
import { AuthPermissions } from 'src/auth/permissions';
import { kitchenDispatchOrderMarkAsCompleted } from 'src/redux/slices/kitchen-dispatch-order';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { TableHeadCustom } from 'src/components/table';

import { IKitchenDispatchOrderDetailedItem } from 'src/types/kitchen-dispatches';

import { paths } from '../../routes/paths';

const LINE_ITEM_HEADERS: TABLE_HEAD_CELL_TYPE[] = [
  { id: 'itemNo', label: '#', align: 'left', primary: true },
  {
    id: 'itemName',
    label: 'Item Name',
    align: 'left',
    primary: true,
    minWidth: 300,
  },
  { id: 'quantity', label: 'Quantity', align: 'left', primary: true },
];

type Props = {
  currentKitchenDispatchOrder: IKitchenDispatchOrderDetailedItem;
};

export default function KitchenDispatchOrderDetails({ currentKitchenDispatchOrder }: Props) {
  const dispatch = useAppDispatcher();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { user: authUser } = useAuthContext();

  const [isLoading, setIsLoading] = useState(false);

  const markAsCompleted = useCallback(async () => {
    setIsLoading(true);
    try {
      await dispatch(
        kitchenDispatchOrderMarkAsCompleted({
          dispatchOrderId: currentKitchenDispatchOrder?._id,
          dispatchOrder: currentKitchenDispatchOrder,
        })
      ).unwrap();
      setIsLoading(false);

      enqueueSnackbar('Kithen dipatch order marked as completed');
      navigate(paths.dashboard.kitchen.dispatch.root());
    } catch (e) {
      if (typeof e === 'string') {
        enqueueSnackbar(e, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar(e?.message, {
          variant: 'error',
        });
      }
      setIsLoading(false);
    }
  }, [dispatch, currentKitchenDispatchOrder, enqueueSnackbar, navigate]);

  const hasRequiredPermissions =
    (currentKitchenDispatchOrder.status === TRANSACTION_STATUS.DRAFT &&
      hasPermission(
        authUser,
        'any',
        AuthPermissions.CREATE_KITCHEN_INVENTORY_TRANSACTION,
        AuthPermissions.UPDATE_KITCHEN_INVENTORY_TRANSACTION,
        AuthPermissions.DELETE_KITCHEN_INVENTORY_TRANSACTION,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )) ||
    (currentKitchenDispatchOrder.status !== TRANSACTION_STATUS.DRAFT &&
      hasPermission(
        authUser,
        'any',
        AuthPermissions.UPDATE_KITCHEN_INVENTORY_TRANSACTION,
        AuthPermissions.DELETE_KITCHEN_INVENTORY_TRANSACTION,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      ));

  return (
    <Grid container spacing={3}>
      <Grid xs={12}>
        <Card sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid
              mb={1}
              display="flex"
              mt={2}
              alignItems="center"
              justifyContent="space-between"
              width={1}
            >
              <Typography variant="subtitle2">Basic Information</Typography>

              {hasRequiredPermissions && (
                <Link
                  component={RouterLink}
                  href={paths.dashboard.kitchen.dispatch.edit(currentKitchenDispatchOrder._id)}
                >
                  <Button variant="text" startIcon={<Iconify icon="solar:pen-bold" />}>
                    Edit
                  </Button>
                </Link>
              )}
            </Grid>

            {currentKitchenDispatchOrder?.status === TRANSACTION_STATUS.COMPLETED && (
              <Grid xs={12} md={4}>
                <ListItemText
                  primary={currentKitchenDispatchOrder?.transactionCode}
                  secondary="Transaction No"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>
            )}

            <Grid xs={12} md={4}>
              <ListItemText
                primary={fDateTime(currentKitchenDispatchOrder?.purchaseOrDispatchDate)}
                secondary="Dispatched Date"
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{
                  component: 'span',
                  color: 'text.disabled',
                }}
              />
            </Grid>

            <Grid xs={12} md={4}>
              <ListItemText
                primary={
                  <Label
                    variant="soft"
                    color={
                      (currentKitchenDispatchOrder?.status === TRANSACTION_STATUS.COMPLETED &&
                        'success') ||
                      (currentKitchenDispatchOrder?.status === TRANSACTION_STATUS.DRAFT &&
                        'default') ||
                      'default'
                    }
                  >
                    {currentKitchenDispatchOrder?.status?.split('_').join(' ')}
                  </Label>
                }
                secondary="Order Status"
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{
                  component: 'span',
                  color: 'text.disabled',
                }}
              />
            </Grid>

            {currentKitchenDispatchOrder?.reason.length > 0 && (
              <Grid xs={12} md={4}>
                <ListItemText
                  primary="Products"
                  secondary={findProducts(currentKitchenDispatchOrder?.reason).map((item) => (
                    <Label variant="soft" color="default" sx={{ mr: 1 }}>
                      {item.label}
                    </Label>
                  ))}
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>
            )}

            <Divider
              sx={{
                width: 1,
                my: 2,
                borderStyle: 'dashed',
              }}
            />

            <Grid mb={1} xs={12} width="full">
              <Typography variant="subtitle2">Line Items</Typography>
            </Grid>

            <Grid xs={12}>
              <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '300px' }}>
                <Scrollbar>
                  <Table stickyHeader size="small" sx={{ minWidth: 960 }}>
                    <TableHeadCustom headLabel={LINE_ITEM_HEADERS} />

                    <TableBody>
                      {currentKitchenDispatchOrder?.items.map((item, index) => (
                        <TableRow hover key={item._id}>
                          <TableCell align="left">
                            <Link
                              component={RouterLink}
                              href={paths.dashboard.kitchen.view(item.inventoryItem._id)}
                            >
                              {item.inventoryItem.itemCode}
                            </Link>
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                            {item.inventoryItem.itemName}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                            {item.quantity} {item.unit.abbreviation}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Scrollbar>
              </TableContainer>
            </Grid>
          </Grid>

          <Stack direction="row" justifyContent="flex-end" gap={2}>
            <Link component={RouterLink} href={paths.dashboard.kitchen.dispatch.root()}>
              <Button variant="outlined" color="inherit">
                Cancel
              </Button>
            </Link>

            {currentKitchenDispatchOrder.status === TRANSACTION_STATUS.DRAFT && (
              <LoadingButton
                type="button"
                variant="contained"
                loading={isLoading}
                onClick={markAsCompleted}
              >
                Mark as completed
              </LoadingButton>
            )}
          </Stack>
        </Card>
      </Grid>
    </Grid>
  );
}
